import { useEffect, useState } from 'react'

import classnames from 'classnames'
import { useTranslation } from 'react-i18next'
import {
  DataItem,
  load,
  navigateTo,
  provideComponent,
  urlForDataItem,
  useDataScope,
  currentPageParams,
} from 'scrivito'

import { DXPMyContactsWidget } from './DXPMyContactsTableWidgetClass'
import personImage from '@/assets/images/profile-generic.svg'
import companyImage from '@/assets/images/profile-organisation.svg'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder'
import { Column } from '@/Atomic/Templates/Table'
import { SegmentFilterModal } from '@/Atomic/Widgets/SegmentFilterModal'
import { DataErrorEditorNote } from '@/Components/DataErrorEditorNote'
import { DataImage } from '@/Components/DataImage'
import { DisplayCountIndicator } from '@/Components/DisplayCountIndicator'
import { EditorNote } from '@/Components/EditorNote'
import { EntityListLoadingIndicator } from '@/Components/EntityListLoadingIndicator'
import { EntityListNoResultsIndicator } from '@/Components/EntityListNoResultsIndicator'
import { EntitySearchHeader } from '@/Components/EntitySearchHeader/EntitySearchHeader'
import { EntityTable } from '@/Components/EntityTable/EntityTable'
import {
  CellRendererParams,
  OpenEntityCanvasAction,
} from '@/Components/EntityTable/types'
import { DATA_SCOPE_ERROR, ErrorVariant } from '@/Components/error/constants'
import { ErrorPage } from '@/Components/error/Errors'
import { Loading } from '@/Components/Loading'
import { showToast } from '@/Components/Toast'
import { ToastMessageType } from '@/Components/Toast/const'
import { DataClassName } from '@/Data/const'
import { ContactContactRelation } from '@/Data/ContactContactRelation/ContactContactRelationDataClass'
import { SchemaAttributes } from '@/Data/types'
import { WhoAmI } from '@/Data/WhoAmI/WhoAmIDataItem'
import { useDataItemReferenceHandler } from '@/hooks/useDataItemReferenceHandler'
import { DataBinary } from '@/utils/dataBinaryToUrl'
import { getDataItemTitle, getSchemaName } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { paramsToFilters } from '@/utils/filterParams'
import { getDataItemPromise } from '@/utils/getDataItemPromise'
import { getDataItems } from '@/utils/getDataItems'
import { getDisplayValue } from '@/utils/getDisplayValue'
import { getRelatedContact } from '@/utils/getRelatedContact'
import { getSchema } from '@/utils/getSchema'
import { getTableColumns } from '@/utils/getTableColumns'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

provideComponent(
  DXPMyContactsWidget,
  () => {
    const { t } = useTranslation()
    const dataScope = useDataScope()
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
    const [filter, setFilter] = useState<SavedFilterStructure | undefined>(
      undefined,
    )

    const contactId = WhoAmI.get('contactId') as string | null

    const params = currentPageParams()
    const search = ensureString(params['search'] ?? null)
    const contextFilter = ensureString(params['filter'] ?? null)
    const query = ensureString(params['query'] ?? null)
    const labels = ensureString(params['labels'] ?? null)
    const attributeFilters = paramsToFilters(params)
    // Remount the loading/no-results indicators whenever the query changes, so
    // Scrivito re-fires their { loading } fallback (it only shows on a fresh
    // mount). Keyed on the full params — stable across load-more (limit is
    // provider state, not a URL param), so the table itself never flashes.
    const queryKey = JSON.stringify(params)

    const { dataItems, error: dataError } = contactId
      ? getDataItems(dataScope)
      : { dataItems: [] as DataItem[] }

    const { schema, error: schemaError } = getSchema(
      getSchemaName(DataClassName.MY_WORK_CONTACT_RELATION),
    )

    const schemaAttributes = getLocalizedSchemaAttributes(
      schema?.get('attributes') as SchemaAttributes,
    )

    const tableColumns = getTableColumns(
      MY_CONTACTS_TABLE_COLS,
      schemaAttributes,
    )

    if (!contactId) {
      return <Loading />
    }

    if (dataError === DATA_SCOPE_ERROR) {
      return <EditorNote>No Data Scope is defined</EditorNote>
    }

    if (dataError) {
      return <DataErrorEditorNote error={dataError} />
    }

    if (!schema || schemaError) {
      return <ErrorPage variant={ErrorVariant.InternalServerError} />
    }

    return (
      <>
        <EntitySearchHeader
          dataClassName={DataClassName.MY_WORK_CONTACT_RELATION}
        >
          <Button
            title={t('common.filter.title', 'Filter')}
            icon="filter"
            variant="outlined"
            onClick={() => setIsFilterModalOpen(true)}
          />
        </EntitySearchHeader>

        <div className="row">
          <div className="col-sm-12">
            <DisplayCountIndicator dataClassName={DataClassName.CONTACT} />
            {(search ||
              contextFilter ||
              query ||
              labels ||
              Object.keys(attributeFilters).length > 0) && (
              <>
                <EntityListLoadingIndicator key={`loading-${queryKey}`} />
                <EntityListNoResultsIndicator key={`no-results-${queryKey}`} />
              </>
            )}
            <EntityTable
              tableColumns={tableColumns}
              dataItems={dataItems}
              schema={schema}
              dataClassName={DataClassName.MY_WORK_CONTACT_RELATION}
              onNavigateTo={onNavigateTo}
              onOpenInNewTab={onOpenInNewTab}
              onOpenEntityCanvasCallbackFn={openContactCanvasAction}
            />
          </div>
        </div>
        <SegmentFilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onSave={(newFilter) => setFilter(newFilter)}
          initialFilter={filter}
          entityName={DataClassName.PERSON}
        />
      </>
    )
  },
  { loading: Loading },
)

const getContact = (dataItem: DataItem) => {
  return getDataItemPromise(
    dataItem.get('contactEntity') as string,
    dataItem.get('contact') as string,
  )
}

const navigateToContact = async (dataItem: DataItem, newTab = false) => {
  const targetDataItem = await getContact(dataItem)
  if (targetDataItem) {
    if (newTab) {
      load(() => {
        urlForDataItem(targetDataItem)
      }).then(() => {
        const url = urlForDataItem(targetDataItem) || undefined
        window.open(url, '_blank')
      })
    } else {
      navigateTo(targetDataItem)
    }
  } else {
    showToast(
      ToastMessageType.ERROR,
      'Error opening contact',
      'Data class or item not found',
    )
  }
}

const onNavigateTo = async (dataItem: DataItem) => {
  navigateToContact(dataItem)
}

const onOpenInNewTab = async (dataItem: DataItem) => {
  navigateToContact(dataItem, true)
}

const openContactCanvasAction: OpenEntityCanvasAction = (
  e,
  dataItem,
  startInEdit,
  base,
) => {
  e.stopPropagation()
  Promise.all([
    getCorrespondingContactSchema(dataItem), // fetch schema before opening canvas
    getContact(dataItem),
  ]).then(([, contact]) => {
    if (contact) {
      base(e, contact, startInEdit)
    }
  })
}

const loadContact = async (
  dataItem: DataItem,
  setContact: (contact: DataItem | null) => void,
) => {
  const contact = await getContact(dataItem)
  setContact(contact)
}

const getCorrespondingContactSchema = async (dataItem: DataItem) => {
  const { schema } = await load(() =>
    getSchema(getSchemaName(dataItem.get('contactEntity') as string)),
  )
  return schema
}

const getLastActivity = (dataItem: DataItem) => {
  return getDataItemPromise(
    DataClassName.ACTIVITY,
    dataItem.get('lastActivity') as string,
  )
}

const loadLastActivity = async (
  dataItem: DataItem,
  setLastActivity: (lastActivity: DataItem | null) => void,
) => {
  const lastActivity = await getLastActivity(dataItem)
  setLastActivity(lastActivity)
}

const getNextActivity = (dataItem: DataItem) => {
  return getDataItemPromise(
    DataClassName.ACTIVITY,
    dataItem.get('nextActivity') as string,
  )
}

const loadNextActivity = async (
  dataItem: DataItem,
  setNextActivity: (nextActivity: DataItem | null) => void,
) => {
  const nextActivity = await getNextActivity(dataItem)
  setNextActivity(nextActivity)
}

const loadActivitySchema = async (
  setActivitySchema: (activitySchema: DataItem | undefined) => void,
) => {
  const { schema } = await load(() =>
    getSchema(getSchemaName(DataClassName.ACTIVITY)),
  )
  setActivitySchema(schema)
}

const Image = ({ dataItem, column: { accessor } }: CellRendererParams) => {
  const [contact, setContact] = useState<DataItem | null>()

  useEffect(() => {
    loadContact(dataItem, setContact)
  }, [dataItem])

  const isCompany = contact?.dataClassName() === DataClassName.COMPANY
  return (
    <DataImage
      dataBinary={contact?.get(accessor) as DataBinary}
      className={classnames(
        'img-profile sm',
        `${isCompany ? 'contain square' : 'cover'}`,
      )}
      alt="contact"
      defaultImage={isCompany ? companyImage : personImage}
    />
  )
}

const Name = ({ dataItem }: CellRendererParams) => {
  const [contact, setContact] = useState<DataItem | null>()

  useEffect(() => {
    loadContact(dataItem, setContact)
  }, [dataItem])

  return getDataItemTitle(contact) || ''
}

const Occasion = ({ dataItem, column: { accessor } }: CellRendererParams) => {
  const [contact, setContact] = useState<DataItem | null>()

  useEffect(() => {
    loadContact(dataItem, setContact)
  }, [dataItem])

  return ensureString(contact?.get(accessor))
}

const LastActivityBegin = ({
  dataItem,
  column: { accessor },
}: CellRendererParams) => {
  const [activitySchema, setActivitySchema] = useState<DataItem>()
  const [lastActivity, setLastActivity] = useState<DataItem | null>()

  useEffect(() => {
    loadActivitySchema(setActivitySchema)
    loadLastActivity(dataItem, setLastActivity)
  }, [dataItem])

  const value = getDisplayValue({
    rawValue: lastActivity?.get(accessor),
    key: accessor,
    schema: activitySchema,
  })

  return value
}

const LastActivityTitle = ({ dataItem }: CellRendererParams) => {
  const [lastActivity, setLastActivity] = useState<DataItem | null>()

  useEffect(() => {
    loadLastActivity(dataItem, setLastActivity)
  }, [dataItem])

  return (
    <StringText
      value={getDataItemTitle(lastActivity) || ''}
      variant="link"
      dataItem={lastActivity ?? undefined}
    />
  )
}

const LastActivityNote = ({
  dataItem,
  column: { accessor },
}: CellRendererParams) => {
  const [lastActivity, setLastActivity] = useState<DataItem | null>()

  useEffect(() => {
    loadLastActivity(dataItem, setLastActivity)
  }, [dataItem])

  return ensureString(lastActivity?.get(accessor))
}

const NextActivityBegin = ({
  dataItem,
  column: { accessor },
}: CellRendererParams) => {
  const [activitySchema, setActivitySchema] = useState<DataItem>()
  const [nextActivity, setNextActivity] = useState<DataItem | null>()

  useEffect(() => {
    loadActivitySchema(setActivitySchema)
    loadNextActivity(dataItem, setNextActivity)
  }, [dataItem])

  const value = getDisplayValue({
    rawValue: nextActivity?.get(accessor),
    key: accessor,
    schema: activitySchema,
  })

  return value
}

const NextActivityType = ({
  dataItem,
  column: { accessor },
}: CellRendererParams) => {
  const [activitySchema, setActivitySchema] = useState<DataItem>()
  const [nextActivity, setNextActivity] = useState<DataItem | null>()

  useEffect(() => {
    loadActivitySchema(setActivitySchema)
    loadNextActivity(dataItem, setNextActivity)
  }, [dataItem])

  const value = getDisplayValue({
    rawValue: nextActivity?.get(accessor),
    key: accessor,
    schema: activitySchema,
  })

  return value
}

const NextActivityTitle = ({ dataItem }: CellRendererParams) => {
  const [nextActivity, setNextActivity] = useState<DataItem | null>()

  useEffect(() => {
    loadNextActivity(dataItem, setNextActivity)
  }, [dataItem])

  return (
    <StringText
      value={getDataItemTitle(nextActivity) || ''}
      variant="link"
      dataItem={nextActivity ?? undefined}
    />
  )
}

const NextActivityAgent = ({
  dataItem,
  column: { accessor },
}: CellRendererParams) => {
  const { get, add } = useDataItemReferenceHandler()
  const [activitySchema, setActivitySchema] = useState<DataItem>()
  const [nextActivity, setNextActivity] = useState<DataItem | null>()

  useEffect(() => {
    loadActivitySchema(setActivitySchema)
    loadNextActivity(dataItem, setNextActivity)
  }, [dataItem])

  const value = getDisplayValue({
    rawValue: nextActivity?.get(accessor),
    key: accessor,
    schema: activitySchema,
    get,
    add,
  })

  return value
}

const TeamMates = ({ dataItem }: CellRendererParams) => {
  const [userId, setUserId] = useState('')
  const [relations, setRelations] = useState<DataItem[]>([])
  const [contacts, setContacts] = useState<(DataItem | null)[]>([])

  const { schema } = getSchema(
    getSchemaName(DataClassName.CONTACT_CONTACT_RELATION),
  )

  useEffect(() => {
    const loadAsync = async () => {
      const contactId = await load(() => WhoAmI.get('contactId') as string)
      const subjectId = dataItem.get('contact') as string

      const relations = await load(() =>
        ContactContactRelation.all()
          .transform({
            filters: {
              father: subjectId,
              contact_isExternal: false,
            },
          })
          .take(),
      )

      const contacts = await Promise.all(
        relations.map((relation) => {
          const { contactId, dataClassName } = getRelatedContact(
            relation,
            subjectId,
          )
          return getDataItemPromise(
            dataClassName || DataClassName.CONTACT,
            contactId,
          )
        }),
      )

      setUserId(contactId)
      setRelations(relations)
      setContacts(contacts)
    }
    loadAsync()
  }, [dataItem])

  return (
    <>
      {relations.map((relation, index) => {
        const contact = contacts[index]

        if (contact?.id() === userId) return null

        const role = getDisplayValue({
          rawValue: relation?.get('role'),
          key: 'role',
          schema,
        })

        let title = getDataItemTitle(contact)
        if (!title) {
          const subjectId = dataItem.get('contact') as string
          const { contactId } = getRelatedContact(relation, subjectId)
          title = ensureString(contactId)
        }
        title += ` (${role})`
        if (index + 1 < relations.length) {
          title += ', '
        }

        return (
          <StringText
            key={relation.id()}
            value={title}
            variant="link"
            dataItem={contact ?? undefined}
          />
        )
      })}
    </>
  )
}

export const MY_CONTACTS_TABLE_COLS = [
  {
    accessor: 'image',
    title: '',
    variant: Column.WIDTH_80PX,
    cell: Image,
  },
  {
    accessor: 'name',
    title: 'Name',
    variant: Column.MAIN,
    cell: Name,
  },
  { accessor: 'role', title: 'Role', variant: Column.WIDTH_80PX },
  {
    accessor: 'team',
    title: 'Team',
    variant: Column.WIDTH_260PX,
    cell: TeamMates,
  },
  {
    accessor: 'contactGroup',
    title: 'Group',
    variant: Column.WIDTH_80PX,
  },
  {
    accessor: 'occasion',
    title: 'Occasion',
    variant: Column.WIDTH_80PX,
    cell: Occasion,
  },
  {
    accessor: 'beginAt',
    title: 'Last Activity Begin',
    variant: Column.WIDTH_80PX,
    cell: LastActivityBegin,
  },
  {
    accessor: 'subject',
    title: 'Last Activity Title',
    variant: Column.WIDTH_80PX,
    cell: LastActivityTitle,
  },
  {
    accessor: 'note',
    title: 'Last Activity Note',
    variant: Column.WIDTH_80PX,
    cell: LastActivityNote,
  },
  {
    accessor: 'beginAt',
    title: 'Next Activity Begin',
    variant: Column.WIDTH_80PX,
    cell: NextActivityBegin,
  },
  {
    accessor: 'type',
    title: 'Next Activity Type',
    variant: Column.WIDTH_80PX,
    cell: NextActivityType,
  },
  {
    accessor: 'subType',
    title: 'Next Activity Subtype',
    variant: Column.WIDTH_80PX,
    cell: NextActivityType,
  },
  {
    accessor: 'subject',
    title: 'Next Activity Title',
    variant: Column.WIDTH_80PX,
    cell: NextActivityTitle,
  },
  {
    accessor: 'agent',
    title: 'Next Activity Agent',
    variant: Column.WIDTH_80PX,
    cell: NextActivityAgent,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]
